"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useState } from "react";

import { useDragScroll } from "@/hooks/use-drag-scroll";

/**
 * Figma 1468:4632 — 설치 장소 전경 가로 스크롤 갤러리(h 451px 사진 스트립 + 우측 화살표).
 * 별도 헤딩 없음(설치 장소 안내 타이틀은 locations 섹션이 담당).
 */
/**
 * 사진별 캡션 데이터 (Figma 1203:5109 — 볼드 설치 유형 + 그레이 "도시 지점명").
 * `type` 은 www.json 설치 유형 키, `city`·`venue` 는 locations 섹션과 동일한 location_* 키.
 * 설치 유형은 지점 성격(실외 레인지 / 실내 연습장 / 스튜디오)으로 분류한 값 —
 * 운영 데이터로 확정된 값이 아니므로 담당자 확인 후 조정 가능.
 */
const PHOTOS = [
  {
    file: "gallery-kor-icheon",
    width: 1203,
    type: "outdoor_driving_range",
    city: "location_korea_icheon",
    venue: "location_korea_icheon_midas_lake_golf_resort",
  },
  {
    file: "gallery-kor-changwon",
    width: 1203,
    type: "outdoor_driving_range",
    city: "location_korea_changwon",
    venue: "location_korea_changwon_utopia_golf_academy",
  },
  {
    file: "gallery-kor-siheung",
    width: 1203,
    type: "outdoor_driving_range",
    city: "location_korea_siheung",
    venue: "location_korea_siheung_saltbay_driving_range",
  },
  {
    file: "gallery-kor-incheon",
    width: 1203,
    type: "indoor_range",
    city: "location_korea_incheon",
    venue: "location_korea_incheon_golf24_hyosung",
  },
  {
    file: "gallery-kor-ulsan",
    width: 1203,
    type: "outdoor_driving_range",
    city: "location_korea_ulsan",
    venue: "location_korea_ulsan_galaxia_golf_club",
  },
  {
    file: "gallery-tw-newtaipei",
    width: 1203,
    type: "outdoor_driving_range",
    city: "location_taiwan_newtaipei",
    venue: "location_taiwan_new_taipei_way_to_golf_2",
  },
  {
    file: "gallery-jp-saitama",
    width: 1197,
    type: "indoor_range",
    city: "location_japan_saitama",
    venue: "location_japan_saitama_flags_golf_school",
  },
  {
    file: "gallery-uk-wexham",
    width: 1203,
    type: "outdoor_driving_range",
    city: "location_uk_slough",
    venue: "location_uk_slough_wexham_park_golf_centre",
  },
  {
    file: "gallery-uk-greenwich",
    width: 1563,
    type: "outdoor_driving_range",
    city: "location_uk_london",
    venue: "location_uk_london_greenwich_peninsula_golf_range",
  },
  {
    file: "gallery-kor-hanmir",
    width: 1201,
    type: "outdoor_driving_range",
    city: "location_korea_daejeon",
    venue: "location_korea_daejeon_hanmir_daedeok_country_club",
  },
  {
    file: "gallery-kor-cheongdam",
    width: 1264,
    type: "indoor_studio",
    city: "location_korea_seoul",
    venue: "location_korea_seoul_choi_wanuk_golf_institute",
  },
  {
    file: "gallery-us-chelsea",
    width: 1203,
    type: "outdoor_driving_range",
    city: "location_usa_newyork",
    venue: "location_usa_newyork_chelsea_piers_golf_club",
  },
  {
    file: "gallery-us-clermont",
    width: 1203,
    type: "outdoor_driving_range",
    city: "location_usa_orlando",
    venue: "location_usa_orlando_clermont_national",
  },
] as const;

export function ApexGallerySection() {
  const t = useTranslations("www");
  // PC 마우스 드래그 + 관성(플링) 스크롤 — 로직은 useDragScroll 공용 훅.
  const { ref: scrollRef, isDragging, handlers } = useDragScroll();
  // 스크롤 양 끝 여부 — 화살표 활성/비활성 색상 결정. update 는 effect 내부 정의(deps 경고 회피).
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = 0; // 최초 위치를 처음(첫 사진)으로 고정
    const update = () => {
      setCanPrev(el.scrollLeft > 1);
      setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
    // scrollRef 는 useRef 객체라 참조가 고정 — deps 에 있어도 재실행되지 않는다.
  }, [scrollRef]);

  const scroll = (direction: number) => {
    scrollRef.current?.scrollBy({ left: direction * 617, behavior: "smooth" });
  };

  return (
    <section className="w-full overflow-hidden bg-gray-900 py-[3.75rem]">
      <div className="flex flex-col gap-9">
        {/* 트랙은 뷰포트 전체 폭을 사용한다(피드백: width 100%) — 넓은 화면에서도
            사진이 컨테이너에 갇히지 않고 화면 끝까지 이어져 스크롤된다. */}
        <div
          ref={scrollRef}
          {...handlers}
          className={`scrollbar-none flex w-full gap-4 overflow-x-auto px-8 pb-4 ${
            isDragging ? "cursor-grabbing" : "cursor-grab"
          }`}
        >
          {/* lg 높이는 Figma 451px 그대로. 28.2rem(=451.2px) 은 0.2px 어긋나
              자연 높이 902 의 정확한 1/2 배율을 깨므로 px 로 고정한다. */}
          {PHOTOS.map(photo => (
            // 래퍼 폭은 사진(높이 고정 + aspect ratio)만으로 결정된다.
            // 캡션은 w-0 min-w-full 로 두어 폭 계산에 기여하지 않게 하고(긴 캡션이
            // 트랙을 밀지 않음) 렌더 시점엔 사진 폭을 그대로 채운다.
            <div
              key={photo.file}
              className="flex shrink-0 flex-col gap-3 select-none"
            >
              <Image
                src={`/images/marketing/apex/gallery/${photo.file}.png`}
                // SD-8403: 설치 매장 사진 — 캡션과 동일한 location_*/type 키 조합 재사용.
                alt={`${t(photo.city)} ${t(photo.venue)} — ${t(photo.type)}`}
                width={photo.width}
                height={902}
                sizes="(max-width: 1024px) 24rem, 37.5rem"
                draggable={false}
                className="h-64 w-auto sm:h-80 lg:h-[451px]"
              />
              {/* Figma 1203:5109 — 볼드 설치 유형 + 그레이 "도시 지점명" */}
              <div className="flex w-0 min-w-full flex-col gap-1">
                <p className="text-body-3 font-bold text-gray-50">
                  {t(photo.type)}
                </p>
                <p className="text-body-3 text-gray-400">
                  {`${t(photo.city)} ${t(photo.venue)}`}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Figma 1874:721 — 우측 정렬 맨 chevron. 이동 가능하면 밝은색(#c7cad1),
            불가하면 현재 색(#6e7581) + disabled. */}
        {/* 트랙과 오른쪽 끝을 맞추기 위해 화살표 행도 전체 폭 사용. */}
        <div className="flex w-full justify-end gap-4 px-8">
          <button
            type="button"
            onClick={() => scroll(-1)}
            disabled={!canPrev}
            aria-label="Previous photos"
            className={`flex size-12 items-center justify-center transition-colors ${
              canPrev ? "text-[#c7cad1]" : "cursor-default text-[#6e7581]"
            }`}
          >
            <ChevronLeft className="size-6" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            disabled={!canNext}
            aria-label="Next photos"
            className={`flex size-12 items-center justify-center transition-colors ${
              canNext ? "text-[#c7cad1]" : "cursor-default text-[#6e7581]"
            }`}
          >
            <ChevronRight className="size-6" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
}
